import type { InjectionKey } from "vue";

export interface VSwitchRowProps {
  title: string
  /**
   * The option's own description, shown under the title and describing the switch for screen readers. It is the
   * option text of the switch, not helper text: explanations about the setting go in `help`.
   */
  description?: string
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
  disabled?: boolean
  /** Why the switch cannot be changed; disables it and shows the reason in a tooltip. */
  disabledReason?: string
  /** The value is being saved or loaded. */
  loading?: boolean
  /** `strong` is the 15px title of a section's main switch, `default` the 14px title of an option. */
  emphasis?: "default" | "strong"
  /** Draws the row as its own bordered card (a section's main switch). Ignored inside a `list` group. */
  card?: boolean
  /** Indents the row inside a `list` group, for an option that depends on the row above. */
  indent?: boolean
  /** A compact row for a menu or toolbar: small switch, regular title, no bleed outside its box. Ignored inside a `list` group. */
  compact?: boolean
  /** Id of the switch input. Defaults to a unique id. */
  id?: string
  testId?: string
}

export interface VSettingRowProps {
  title: string
  /**
   * The setting's own description, shown under the title and describing the control for screen readers. Explanations
   * about the setting go in `help`. The `description` slot replaces it with rich content (a link button).
   */
  description?: string
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
  /** Shows the red required mark after the title. Pass the rule the form validates, never a constant. */
  required?: boolean
  /** Indents the row inside a `list` group, for a setting that depends on the row above. */
  indent?: boolean
  /** Gives the control the fixed 240px column of a select; without it the control takes its own width. */
  wideControl?: boolean
  /** Id of the control; the title is its label. Defaults to a unique id. */
  id?: string
  testId?: string
}

/** What the control slot of `VSettingRow` binds on the control. */
export interface VSettingRowControlSlotProps {
  /** Id for the control, which the title label points at. */
  id: string
  /** Id of the title, for the `aria-labelledby` of a control that is not a native labelable element. */
  labelId: string
  /** Id of the description, for the control's `aria-describedby`; undefined without a `description`. */
  describedBy: string | undefined
}

export interface VSwitchRowGroupProps {
  /**
   * `list`: a `VListCard`, one bordered box with a divider between the rows (guest app settings).
   * `nested`: options under a main switch, indented behind a left border (Booking Experts synchronisation).
   */
  variant?: "list" | "nested"
  /**
   * Makes every row of a `list` group at least 56px high, so switch rows and setting rows with a select line up (the
   * guest link settings). Other lists keep rows as high as their content.
   */
  tall?: boolean
}

export const SWITCH_ROW_LIST_KEY: InjectionKey<boolean> = Symbol("switchRowList");

/** Whether the rows of the surrounding `list` group are at least 56px high (`VSwitchRowGroup` `tall`). */
export const SWITCH_ROW_TALL_KEY: InjectionKey<boolean> = Symbol("switchRowTall");
